import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createWizard } from "../../../../Components/Utils/Wizard";
import { useHistoricoContext } from "../../../../Context/Historico";
import { ListarParcelasBoletosController } from "../../../../Controllers/Cobranca/ParcelasAbertas/ParcelasAbertasController";
import {
    CalcularDescontosRenegociacaoController,
    CalcularNovoFluxoController,
    CarregarParcelasModeloRenegociacaoController,
    FinalizarAcordoRenegociacaoController,
} from "../../../../Controllers/Cobranca/Renegociacao/RenegociacaoControllers";
import {
    IParcelaBoletos,
    IParcelas,
} from "../../../../Interfaces/Cobranca/ParcelasAbertas/ParcelasAbertas";
import {
    IModeloVenda,
    IParcelaModelo,
} from "../../../../Interfaces/Cobranca/Renegociacao/ModeloRenegociacao/ModeloRenegociacaoInterface";
import {
    IDescontos,
    IGerarNovoFluxoRequest,
    IIndicadoresParcelasRenegociacao,
    IParcelaGeradaNovoFluxo,
    IParcelasComDescontos,
} from "../../../../Interfaces/Cobranca/Renegociacao/RenegociacaoInterfaces";
import {
    arrayEquality,
    objectEquality,
} from "../../../../Tools/ProcessingTools";
import StorageManagerCapys from "../../../../Tools/Storage";
import EscolhaDeModelo from "./EscolhaDeModelo";
import EscolhaDeParcelas from "./EscolhaDeParcelas";
import EscolhaDescontos from "./EscolhaDescontos";
import NovoFluxo from "./NovoFluxo";

//#region  interfaces & types

type TStepKeys =
    | "selecParcelas"
    | "selecDescontos"
    | "selecModelo"
    | "novoFluxo";

interface IParcelasReqDTCalculo
    extends Omit<
        IParcelas,
        "NR_EMPRESA" | "DS_OBRA" | "NR_VENDA" | "FL_CARREGAR_TODAS_PARCELAS"
    > {}
interface IRegistrarAcordoProps extends IParcelas {
    idCaso: number;
    idProduto: number;
    setShow: (show: boolean) => void;

    lstModelos: Array<IModeloVenda>;
}
interface ICalculoNovoFluxoRequest {
    parcelas: Array<IParcelaModelo>;
    request: IGerarNovoFluxoRequest;
}

interface IRegistrarAcordoContext {
    // wizard
    goToStep: (step: TStepKeys) => void;

    //escolha de parcelas
    parcelasReq: IParcelasReqDTCalculo;
    setParcelasReq: (parcelasReq: IParcelasReqDTCalculo) => void;
    lstTodasParcelas: {
        get: () => Promise<Array<IParcelaBoletos>>;
    };
    lstParcelasSelecionadas?: Array<IParcelaBoletos>;
    lstParcelasSelecionadasComDescontos?: Array<IParcelasComDescontos>;
    selecionarParcelas: (
        parcelasSelecionadas: Array<IParcelasComDescontos>
    ) => void;

    //indicadores
    indicadores: IIndicadoresParcelasRenegociacao | undefined;
    setIndicadores: (indicadores: IIndicadoresParcelasRenegociacao) => void;

    //calculo de descontos
    descontosRef: React.MutableRefObject<IDescontos | undefined>;
    calcularDescontos: (descontos: IDescontos) => Promise<void>;

    //escolha de novo modelo de parcelas
    modelos: {
        getModelos: () => Promise<Array<IModeloVenda>>;
        getParcelasModelo: (
            idModelo: number,
            DiaPrimeiraParc: number,
            valorEntrada?: number,
            dtEntrada?: string
        ) => Promise<Array<IParcelaModelo> | undefined>;
    };
    calculoNovoFluxoRequest: React.MutableRefObject<
        ICalculoNovoFluxoRequest | undefined
    >;

    // calculo de novo fluxo
    calcularNovoFluxo: (
        parcelas: Array<IParcelaModelo>,
        idModelo: number,
        DiaPrimeiraParc: number,
        ValorEntrada: number,
        diff: boolean
    ) => void;
    novoFluxo: {
        get: () => Promise<Array<IParcelaGeradaNovoFluxo> | undefined>;
    };

    finalizarAcordo: () => Promise<void>;
    setDiaEEntrada: (value: any) => void;
    setValoresFluxo: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    valoresFluxo: number[] | undefined;
    setSomaPrclsTotal: React.Dispatch<React.SetStateAction<number>>;
    somaPrclsTotal: number;
}

interface IRenegociacaoCache {
    step?: TStepKeys;
    lstTodasParcelas?: Array<IParcelaBoletos>;
    lstParcelasSelecionadas?: Array<IParcelaBoletos>;
    lstParcelasSelecionadasComDescontos?: Array<IParcelasComDescontos>;
    indicadores?: IIndicadoresParcelasRenegociacao | undefined;
    descontos?: IDescontos;
    idModelo?: number;
    parcelasModeloMap?: Array<[string, Array<IParcelaModelo>]>; // mapa manual [value, value]
    calculoNovoFluxoRequest?: ICalculoNovoFluxoRequest;
    novoFluxo?: Array<IParcelaGeradaNovoFluxo>;
}

//#endregion

const etapas: Array<TStepKeys> = [
    "selecParcelas",
    "selecDescontos",
    "selecModelo",
    "novoFluxo",
];

// factories
const { Wizard, WizardStep, useWizard } = createWizard(etapas);

const RegistrarAcordoContext = createContext<IRegistrarAcordoContext>(
    {} as IRegistrarAcordoContext
);

export default function RegistrarAcordo(props: IRegistrarAcordoProps) {
    // cashe
    const casheManager = useRef(
        new StorageManagerCapys<IRenegociacaoCache>(
            `RENEGOCIACAO_${props.idCaso}_${props.NR_EMPRESA}_${props.DS_OBRA}_${props.NR_VENDA}`,
            "session"
        )
    );
    const cashe = casheManager.current.get();
    //externo
    const { setShow } = props;
    const { ReloadHistorico } = useHistoricoContext();
    const valorEntrada = cashe?.calculoNovoFluxoRequest?.request.ValorEntrada;

    //wizard
    const etapasInfo = useMemo(
        () => ({
            selecParcelas: {
                title: "Seleção de Parcelas",
                reset: () => {},
            },
            selecDescontos: {
                title: "Descontos",
                reset: () => {
                    descontosRef.current = undefined;
                    setLstParcelasSelecionadasComDescontos(undefined);
                },
            },
            selecModelo: {
                title: "Modelo de Renegociação",
                reset: () => {
                    calculoNovoFluxoRequest.current = undefined;
                },
            },
            novoFluxo: {
                title: "Novo Fluxo",
                reset: () => {
                    lstNovoFluxo.current = undefined;
                },
            },
        }),
        []
    );
    const wizard = useWizard({
        startAtStep: cashe?.step,
    });
    const { changeStep, visiteds, resetStep } = wizard;
    const { goTo: goToStep } = changeStep;
    const [blockBreadCrumb, setBlockBreadCrumb] = useState(false);

    const resetPosStep = useCallback(
        (step: TStepKeys) => {
            resetStep(step);
            const indexEtapa = etapas.findIndex((etapa) => etapa === step);
            for (let index = indexEtapa + 1; index < etapas.length; index++) {
                const etapa = etapas[index];
                etapasInfo?.[etapa]?.reset?.();
            }
        },
        [resetStep, etapasInfo]
    );

    //listagem de parcelas
    const [parcelasReq, setParcelasReq] = useState<IParcelasReqDTCalculo>({
        DT_CALCULO: new Date().toISOString().substring(0, 10),
        FL_ANTECIPACAO: false,
    });
    const lstParcelas = useRef(cashe?.lstTodasParcelas);
    const [lstParcelasSelecionadas, setLstParcelasSelecionadas] = useState(
        cashe?.lstParcelasSelecionadas
    );
    const [
        lstParcelasSelecionadasComDescontos,
        setLstParcelasSelecionadasComDescontos,
    ] = useState(cashe?.lstParcelasSelecionadasComDescontos);

    //descontos
    const descontosRef = useRef(cashe?.descontos);

    //modelos
    const idModeloRef = useRef(cashe?.idModelo);
    const parcelasModelos = useRef(
        cashe?.parcelasModeloMap
            ? new Map(cashe?.parcelasModeloMap)
            : new Map<string, Array<IParcelaModelo>>()
    );

    //novo fluxo
    const calculoNovoFluxoRequest = useRef(cashe?.calculoNovoFluxoRequest);
    const lstNovoFluxo = useRef(cashe?.novoFluxo);

    const [indicadores, setIndicadores] = useState(cashe?.indicadores);
    const [diaEEntrada, setDiaEEntrada] = useState<any>({
        DataVencimentoEntrada: "",
        DiaPrimeiraParc: "",
        ValorEntrada: 0,
    });
    const [valoresFluxo, setValoresFluxo] = useState<number[] | undefined>([]);
    const [somaPrclsTotal, setSomaPrclsTotal] = useState(0);

    useEffect(() => {
        const manager = casheManager.current;
        return () =>
            manager.set({
                descontos: descontosRef.current,
                idModelo: idModeloRef.current,
                indicadores: indicadores,
                lstTodasParcelas: lstParcelas.current,
                lstParcelasSelecionadas: lstParcelasSelecionadas,
                lstParcelasSelecionadasComDescontos:
                    lstParcelasSelecionadasComDescontos,
                parcelasModeloMap: Array.from(
                    parcelasModelos.current.entries()
                ),
                novoFluxo: lstNovoFluxo.current,
                calculoNovoFluxoRequest: calculoNovoFluxoRequest.current,
                step: visiteds[visiteds.length - 1],
            });
    }, [
        indicadores,
        lstParcelasSelecionadas,
        lstParcelasSelecionadasComDescontos,
        visiteds,
    ]);

    const lstTodasParcelas = useMemo(
        () => ({
            get: async () => {
                if (lstParcelas.current) return lstParcelas.current;
                else {
                    return ListarParcelasBoletosController({
                        NR_EMPRESA: props.NR_EMPRESA,
                        DS_OBRA: props.DS_OBRA,
                        NR_VENDA: props.NR_VENDA,
                        ...parcelasReq,
                    }).then((parcelas) => {
                        lstParcelas.current = parcelas;
                        return parcelas;
                    });
                }
            },
        }),
        [
            lstParcelas,
            props.NR_EMPRESA,
            props.DS_OBRA,
            props.NR_VENDA,
            parcelasReq,
        ]
    );

    const selecionarParcelas = useCallback(
        (parcelasSelecionadas: Array<IParcelasComDescontos>) => {
            if (
                !lstParcelasSelecionadas ||
                !arrayEquality(parcelasSelecionadas, lstParcelasSelecionadas)
            ) {
                setLstParcelasSelecionadas(parcelasSelecionadas);
                resetPosStep("selecParcelas");
            }
        },
        [lstParcelasSelecionadas, resetPosStep]
    );

    const calcularDescontos = useCallback(
        async (descontos: IDescontos) => {
            if (
                lstParcelasSelecionadas &&
                !objectEquality(descontos, descontosRef.current)
            ) {
                await CalcularDescontosRenegociacaoController(
                    props.idCaso,
                    lstParcelasSelecionadas,
                    descontos
                ).then((res) => {
                    // faz o merge de parcelas e descontos e extrai os indicadores
                    const { indicadores, parcelas } = res.reduce(
                        (
                            { indicadores, parcelas },
                            parcelaComDesconto,
                            index
                        ) => {
                            const parcela = lstParcelasSelecionadas[index];
                            parcelas.push({
                                ...parcela,
                                vlrDescontoJuros:
                                    parcelaComDesconto.vlrDescontoJuros,
                                vlrDesconto: parcelaComDesconto.vlrDesconto,
                                vlrTotalComDesconto:
                                    parcelaComDesconto.vlrTotalComDesconto,
                                vlrDescontoMulta:
                                    parcelaComDesconto.vlrDescontoMulta,
                                vlrHonorario: parcelaComDesconto.vlrHonorario,
                                vlrComDesconto:
                                    parcelaComDesconto.vlrComDesconto,
                                vlrTotal: parcelaComDesconto.vlrTotal,
                                ValorCustas: parcelaComDesconto.ValorCustas,
                                ValorContrato: parcelaComDesconto.ValorContrato,
                                ValorSeguro: parcelaComDesconto.ValorSeguro,
                            });

                            return {
                                indicadores: {
                                    VL_CONTRATO:
                                        indicadores.VL_CONTRATO +
                                        (parcelaComDesconto.ValorContrato ?? 0),
                                    VL_CUSTAS:
                                        indicadores.VL_CUSTAS +
                                        (parcelaComDesconto.ValorCustas ?? 0),
                                    VL_SEGURO:
                                        indicadores.VL_SEGURO +
                                        (parcelaComDesconto.ValorSeguro ?? 0),
                                    VL_HONORARIO:
                                        indicadores.VL_HONORARIO +
                                        (parcelaComDesconto.vlrHonorario ?? 0),
                                    VL_DESCONTO:
                                        indicadores.VL_DESCONTO +
                                        (parcelaComDesconto.vlrDesconto ?? 0) +
                                        (parcelaComDesconto.vlrDescontoMulta ??
                                            0) +
                                        (parcelaComDesconto.vlrDescontoJuros ??
                                            0),
                                    VL_TOTAL:
                                        indicadores.VL_TOTAL +
                                        (parcelaComDesconto.vlrTotalComDesconto ??
                                            0),
                                },
                                parcelas,
                            };
                        },
                        {
                            indicadores: {
                                VL_CONTRATO: 0,
                                VL_CUSTAS: 0,
                                VL_SEGURO: 0,
                                VL_HONORARIO: 0,
                                VL_DESCONTO: 0,
                                VL_TOTAL: 0,
                            },
                            parcelas: new Array<IParcelasComDescontos>(),
                        }
                    );

                    setIndicadores(indicadores);
                    setLstParcelasSelecionadasComDescontos(parcelas);
                    descontosRef.current = descontos;
                    resetPosStep("selecDescontos");
                    // reseta o map de parcelas modelos sempre que os indicadores mudam
                    parcelasModelos.current = new Map();
                });
            }
        },
        [lstParcelasSelecionadas, props.idCaso, resetPosStep]
    );
    const modelos = useMemo(
        () => ({
            getModelos: async () => props.lstModelos,
            getParcelasModelo: async (
                idModelo: number,
                DiaPrimeiraParc: number,
                valorEntrada?: number,
                dtEntrada?: string
            ) => {
                const mapKey = `${idModelo}_${DiaPrimeiraParc}`;
                if (parcelasModelos.current.has(mapKey))
                    return parcelasModelos.current.get(mapKey);
                else if (indicadores) {
                    return CarregarParcelasModeloRenegociacaoController(
                        idModelo,
                        DiaPrimeiraParc,
                        indicadores,
                        valorEntrada,
                        dtEntrada
                    ).then((parcelasModelo) => {
                        parcelasModelos.current.set(mapKey, parcelasModelo);
                        return parcelasModelo;
                    });
                }
            },
        }),
        [props.lstModelos, indicadores]
    );

    const novoFluxo = useMemo(
        () => ({
            get: async () => {
                if (lstNovoFluxo.current) return lstNovoFluxo.current;
                else if (calculoNovoFluxoRequest.current) {
                    return CalcularNovoFluxoController(
                        calculoNovoFluxoRequest.current.parcelas,
                        calculoNovoFluxoRequest.current.request
                    ).then((fluxo) => {
                        lstNovoFluxo.current = fluxo;
                        return fluxo;
                    });
                }
            },
        }),
        []
    );

    const calcularNovoFluxo = useCallback(
        (
            parcelas: Array<IParcelaModelo>,
            idModelo: number,
            DiaPrimeiraParc: number,
            ValorEntrada: number,
            diff: boolean
        ) => {
            if (indicadores) {
                idModeloRef.current = idModelo;
                calculoNovoFluxoRequest.current = {
                    parcelas,
                    request: {
                        DiaPrimeiraParc,
                        idModelo,
                        idCaso: props.idCaso,
                        valores: { ...indicadores },
                        ValorEntrada,
                    },
                };
                if (diff) resetPosStep("selecModelo");
            }
        },
        [props.idCaso, indicadores, resetPosStep]
    );

    const finalizarAcordo = useCallback(async () => {
        if (
            lstParcelasSelecionadasComDescontos &&
            lstNovoFluxo.current &&
            idModeloRef.current &&
            descontosRef.current &&
            indicadores
        ) {
            setBlockBreadCrumb(true);
            return FinalizarAcordoRenegociacaoController(
                {
                    LstParcelasAntigoFluxo: lstParcelasSelecionadasComDescontos,
                    LstParcelasNovoFluxo: lstNovoFluxo.current,
                    IdCaso: props.idCaso,
                    IdProduto: props.idProduto,
                    FlRenegociarFluxo: true,
                    idModelo: idModeloRef.current,
                    DescontoFinanceiro: indicadores.VL_DESCONTO,
                    AcrescimoFinanceiro: indicadores.VL_HONORARIO,
                    ValorTotal: indicadores.VL_TOTAL,
                    DataDeCalculo: parcelasReq.DT_CALCULO,
                },
                {
                    ...diaEEntrada,
                    ValorContrato: indicadores.VL_CONTRATO,
                    ValorCustas: indicadores.VL_CUSTAS,
                    ValorSeguro: indicadores.VL_SEGURO,
                    ValorHonorario: indicadores.VL_HONORARIO,
                    ValorTotal: indicadores.VL_TOTAL,
                    ValorEntrada: valorEntrada,
                }
            )
                .then(() => {
                    setShow(false);
                    ReloadHistorico?.();
                })
                .finally(() => setBlockBreadCrumb(false));
        }
    }, [
        lstParcelasSelecionadasComDescontos,
        indicadores,
        diaEEntrada,
        props.idCaso,
        props.idProduto,
        parcelasReq.DT_CALCULO,
        setShow,
        ReloadHistorico,
        valorEntrada,
    ]);

    return (
        <RegistrarAcordoContext.Provider
            value={{
                goToStep,
                setParcelasReq: (newParcelasReq) => {
                    lstParcelas.current = undefined;
                    setParcelasReq({ ...parcelasReq, ...newParcelasReq });
                },
                parcelasReq,
                lstTodasParcelas,
                lstParcelasSelecionadas,
                lstParcelasSelecionadasComDescontos,
                selecionarParcelas,

                indicadores,
                setIndicadores,

                descontosRef,
                calcularDescontos,

                modelos,
                calculoNovoFluxoRequest,

                calcularNovoFluxo,
                novoFluxo,
                finalizarAcordo,
                setDiaEEntrada,
                setValoresFluxo,
                valoresFluxo,
                setSomaPrclsTotal,
                somaPrclsTotal,
            }}
        >
            <Wizard
                stepInfo={etapasInfo}
                blockBreadCrumb={blockBreadCrumb}
                unmountOnExit
                {...wizard}
            >
                <WizardStep stepKey="selecParcelas">
                    <EscolhaDeParcelas />
                </WizardStep>
                <WizardStep stepKey="selecDescontos">
                    <EscolhaDescontos />
                </WizardStep>
                <WizardStep stepKey="selecModelo">
                    <EscolhaDeModelo />
                </WizardStep>
                <WizardStep stepKey="novoFluxo">
                    <NovoFluxo />
                </WizardStep>
            </Wizard>
        </RegistrarAcordoContext.Provider>
    );
}

export function useRegistrarAcordoContext() {
    const context = useContext(RegistrarAcordoContext);
    if (!context)
        throw new Error(
            "useRegistrarAcordoContext necessita de um RegistrarAcordoProvider em volta do que sera utilizado"
        );

    return context;
}
